import type { z } from 'zod';
import { CURRENT_SCHEMA_VERSION, projectSchema, type Project } from './project';

/**
 * Project JSON migrations. `MIGRATIONS[n]` upgrades a version-n document to n + 1.
 * Whenever the model changes shape: bump CURRENT_SCHEMA_VERSION, add a migration here and a
 * fixture test for the old version (CLAUDE.md "API").
 */
type Json = Record<string, unknown>;
type Migration = (doc: Json) => Json;

export const MIGRATIONS: Readonly<Record<number, Migration>> = {
  /** v2 adds investments (returns analysis); existing projects have none. */
  1: (doc) => ({ ...doc, investments: [] }),
  /** v3 adds key dates; existing projects have none. */
  2: (doc) => ({ ...doc, keyDates: [] }),
};

export type ParseProjectResult =
  | { success: true; data: Project; migratedFrom: number | null }
  | {
      success: false;
      error: 'notAnObject' | 'missingVersion' | 'futureVersion' | 'noMigration' | 'invalid';
      issues: z.core.$ZodIssue[];
    };

function isObject(value: unknown): value is Json {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Upgrades any supported stored project to the current schema and validates it. */
export function parseProject(raw: unknown): ParseProjectResult {
  if (!isObject(raw)) return { success: false, error: 'notAnObject', issues: [] };
  const version = raw.schemaVersion;
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
    return { success: false, error: 'missingVersion', issues: [] };
  }
  if (version > CURRENT_SCHEMA_VERSION)
    return { success: false, error: 'futureVersion', issues: [] };

  let doc: Json = raw;
  for (let v = version; v < CURRENT_SCHEMA_VERSION; v++) {
    const migrate = MIGRATIONS[v];
    if (!migrate) return { success: false, error: 'noMigration', issues: [] };
    doc = { ...migrate(doc), schemaVersion: v + 1 };
  }

  const parsed = projectSchema.safeParse(doc);
  if (!parsed.success) return { success: false, error: 'invalid', issues: parsed.error.issues };
  return {
    success: true,
    data: parsed.data,
    migratedFrom: version === CURRENT_SCHEMA_VERSION ? null : version,
  };
}
